/** Ported from chief's `web/src/ui.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

import type { ContentStatus, IdeaStatus, StepStatus } from './api.ts';
import { Icon, type IconName } from './Icon.tsx';
import { type MarkdownBlock, parseMarkdown, type TextRun } from './markdown.ts';
import { Link } from './router.tsx';
import { IDEA_STATUS } from './wording.ts';

/**
 * The small vocabulary every page is built from. Kept as plain components over
 * the BEM classes in `styles/` so a page never has to know a class name for a
 * badge or a progress bar, and so every page's badge is the same badge.
 */

export type Tone = 'neutral' | 'ready' | 'active' | 'wait' | 'done' | 'review' | 'final' | 'danger';

/**
 * Every state string the app produces maps to exactly one tone, so a content,
 * a step and a log connection that mean the same thing look the same.
 */
export const CONTENT_TONE: Record<ContentStatus, Tone> = {
  pending: 'neutral',
  ready: 'ready',
  producing: 'active',
  waiting: 'wait',
  // The gate reads like the hold it is — paused, not broken — and it is the
  // operator's move rather than the clock's, which the label says.
  approval: 'wait',
  // The purple pair is the end of the line: a version waiting on the operator
  // reads (review), and the one they approved is the deeper delivered purple.
  review: 'review',
  delivered: 'final',
  // Finished but must not be used: red like a failure, which it is in effect.
  rejected: 'danger',
  failed: 'danger',
};

/** An idea's status, in the same tones: drafted waits on the operator like a review. */
export const IDEA_STATUS_TONE: Record<IdeaStatus, Tone> = {
  raw: 'neutral',
  working: 'active',
  drafted: 'review',
  approved: 'done',
  declined: 'neutral',
  failed: 'danger',
};

export const STEP_TONE: Record<StepStatus, Tone> = {
  todo: 'neutral',
  'in-progress': 'active',
  done: 'done',
};

/** What the operator reads for each state. */
export const CONTENT_LABEL: Record<ContentStatus, string> = {
  pending: 'planning',
  ready: 'ready',
  producing: 'producing',
  waiting: 'on hold',
  approval: 'needs your approval',
  review: 'in review',
  delivered: 'delivered',
  rejected: 'rejected',
  failed: 'failed',
};

/** Statuses with an agent of their own running: worth a moving dot. */
function isRunning(status: ContentStatus): boolean {
  return status === 'producing';
}

export function Badge({
  tone = 'neutral',
  pulse = false,
  children,
  title,
}: {
  readonly tone?: Tone;
  /** A moving dot: the one cue that says "running right now". */
  readonly pulse?: boolean;
  readonly children: ReactNode;
  readonly title?: string;
}) {
  return (
    <span className={`badge badge--${tone}${pulse ? ' badge--pulse' : ''}`} title={title}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { readonly status: ContentStatus }) {
  return (
    <Badge tone={CONTENT_TONE[status]} pulse={isRunning(status)}>
      {CONTENT_LABEL[status]}
    </Badge>
  );
}

/** Where one idea stands, as the batch page's rows and the board's cards show it. */
export function IdeaStatusBadge({ status, title }: { readonly status: IdeaStatus; readonly title?: string }) {
  return (
    <Badge tone={IDEA_STATUS_TONE[status]} pulse={status === 'working'} {...(title === undefined ? {} : { title })}>
      {IDEA_STATUS[status]}
    </Badge>
  );
}

/** A coloured dot on its own, for dense rows where a pill is too loud. */
export function StatusDot({ status }: { readonly status: ContentStatus }) {
  return (
    <span
      className={`dot dot--${CONTENT_TONE[status]}${isRunning(status) ? ' dot--pulse' : ''}`}
      title={CONTENT_LABEL[status]}
      aria-label={CONTENT_LABEL[status]}
      role="img"
    />
  );
}

const NOTICE_ICON: Record<'ok' | 'error' | 'warn' | 'info' | 'wait', IconName> = {
  ok: 'check-circle',
  error: 'x-circle',
  warn: 'alert',
  info: 'info',
  wait: 'clock',
};

export function Notice({
  kind,
  children,
  role,
  className,
}: {
  /** `wait` is a hold the operator sits out, not a problem to act on. */
  readonly kind: 'ok' | 'error' | 'warn' | 'info' | 'wait';
  readonly children: ReactNode;
  readonly role?: 'alert' | 'status';
  readonly className?: string;
}) {
  return (
    <div
      className={`notice notice--${kind}${className === undefined ? '' : ` ${className}`}`}
      role={role ?? (kind === 'error' ? 'alert' : 'status')}
    >
      <Icon name={NOTICE_ICON[kind]} />
      <div className="notice__body">{children}</div>
    </div>
  );
}

/** Step progress as a bar, with the fraction beside it. */
export function Progress({
  done,
  total,
  tone = 'active',
  compact = false,
  label,
}: {
  readonly done: number;
  readonly total: number;
  readonly tone?: Tone;
  readonly compact?: boolean;
  readonly label?: string;
}) {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className={`progress${compact ? ' progress--compact' : ''}`}>
      <div
        className={`progress__track progress__track--${tone}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={label ?? 'Steps done'}
      >
        <div className="progress__fill" style={{ width: `${String(percent)}%` }} />
      </div>
      <span className="progress__label mono">
        {total === 0 ? '—' : `${String(done)}/${String(total)}`}
      </span>
    </div>
  );
}

/**
 * Bytes as gigabytes, one decimal below 10 GB and whole numbers above it.
 *
 * Shared by the sidebar's memory row and the overview's machine tiles, so one
 * host reading is never written two ways.
 */
export function gigabytes(bytes: number): string {
  const value = bytes / 1024 ** 3;
  return value >= 10 ? String(Math.round(value)) : value.toFixed(1);
}

/**
 * Bytes with the unit that makes them readable — a bucket holds kilobytes of
 * plan and gigabytes of render, and the same reading has to work for both.
 *
 * Binary units, because that is what every object store reports and what an
 * operator will see in their own bucket browser.
 */
export function bytesLabel(bytes: number): string {
  if (bytes < 1024) return `${String(Math.round(bytes))} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? String(Math.round(value)) : value.toFixed(1)} ${units[unit] ?? 'TB'}`;
}

/**
 * A fraction of something continuous — host CPU, host memory — as a short bar.
 *
 * `Meter` counts whole production slots and `Progress` carries its own label; this
 * is the one for a ratio that sits at the end of a status row, where the number
 * beside it is already spelled out.
 */
export function Gauge({ value, label }: { readonly value: number; readonly label: string }) {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  // The colour is the warning: quiet while there is room, amber as it fills,
  // red once the machine has nothing left to give.
  const tone: Tone = percent >= 90 ? 'danger' : percent >= 75 ? 'wait' : 'active';
  return (
    <div
      className={`gauge gauge--${tone}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={label}
    >
      <div className="gauge__fill" style={{ width: `${String(percent)}%` }} />
    </div>
  );
}

/** Production slots as one cell per slot. */
export function Meter({
  value,
  max,
  label,
}: {
  readonly value: number;
  readonly max: number;
  readonly label: string;
}) {
  const cells = Math.max(1, Math.min(max, 12));
  return (
    <div className="meter" role="img" aria-label={label}>
      {Array.from({ length: cells }, (_, index) => (
        <span
          className={`meter__cell${index < Math.min(value, cells) ? ' meter__cell--on' : ''}`}
          key={index}
        />
      ))}
      {max > cells && <span className="meter__more">+{max - cells}</span>}
    </div>
  );
}

/** A number the overview reports, with what it is. */
export function Figure({
  label,
  value,
  hint,
  tone,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  readonly tone?: Tone;
}) {
  return (
    <div className={`figure${tone === undefined ? '' : ` figure--${tone}`}`}>
      <span className="figure__value">{value}</span>
      <span className="figure__label">{label}</span>
      {hint !== undefined && <span className="figure__hint">{hint}</span>}
    </div>
  );
}

/**
 * A bar per day. Inline SVG rather than a chart library: one series, no axes
 * to speak of, and the number that matters is written next to it.
 */
export function Bars({
  values,
  labels,
  ariaLabel,
}: {
  readonly values: readonly number[];
  readonly labels: readonly string[];
  readonly ariaLabel: string;
}) {
  const max = Math.max(1, ...values);
  const width = 100;
  const height = 36;
  const gap = 1.5;
  const bar = (width - gap * (values.length - 1)) / Math.max(1, values.length);
  return (
    <svg
      className="bars"
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      {values.map((value, index) => {
        const h = value === 0 ? 1 : Math.max(2, (value / max) * height);
        return (
          <rect
            key={labels[index] ?? index}
            className={value === 0 ? 'bars__bar bars__bar--empty' : 'bars__bar'}
            x={index * (bar + gap)}
            y={height - h}
            width={bar}
            height={h}
            rx={0.6}
          >
            <title>{`${labels[index] ?? ''}: ${String(value)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  readonly icon: IconName;
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__icon">
        <Icon name={icon} className="icon--lg" />
      </span>
      <h3 className="empty__title">{title}</h3>
      {children !== undefined && <div className="empty__body">{children}</div>}
      {action !== undefined && <div className="empty__action">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  eyebrow,
  subtitle,
  actions,
  back,
}: {
  readonly title: ReactNode;
  /** A line above the title: a repository name, or a section. */
  readonly eyebrow?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
  readonly back?: { readonly href: string; readonly label: string };
}) {
  return (
    <header className="page__header">
      <div className="page__heading">
        {back !== undefined && (
          <Link className="page__back" href={back.href}>
            <Icon name="arrow-left" />
            {back.label}
          </Link>
        )}
        {eyebrow !== undefined && <div className="page__eyebrow">{eyebrow}</div>}
        <h1 className="page__title">{title}</h1>
        {subtitle !== undefined && <p className="page__subtitle">{subtitle}</p>}
      </div>
      {actions !== undefined && <div className="page__actions">{actions}</div>}
    </header>
  );
}

/** A titled surface. The product's one container shape. */
export function Panel({
  title,
  icon,
  meta,
  actions,
  children,
  tone,
  className,
  id,
  collapsible = false,
  defaultOpen = true,
  summary,
  open: controlledOpen,
  onOpenChange,
}: {
  readonly title?: ReactNode;
  readonly icon?: IconName;
  /** Sits beside the title: a badge, a count. */
  readonly meta?: ReactNode;
  /** Shown on the right of the header; on a collapsible panel only while open. */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly tone?: 'danger' | 'warn';
  readonly className?: string;
  /** Also keys a collapsible panel's remembered state and its `#<id>` anchor. */
  readonly id?: string;
  /** Folds the body behind a header button. */
  readonly collapsible?: boolean;
  /** A collapsible panel's state when nothing has been remembered for it. */
  readonly defaultOpen?: boolean;
  /** The section's state in one line, shown in place of `actions` while collapsed. */
  readonly summary?: ReactNode;
  /**
   * A collapsible panel whose owner has to know whether it is open — to start
   * a poll or a socket only then — holds the state itself with
   * {@link usePanelOpen} and passes it in here with `onOpenChange`.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(() => !collapsible || readPanelOpen(id, defaultOpen));
  const open = !collapsible || (controlledOpen ?? ownOpen);
  // A ref, so the effects below keep one listener rather than re-binding on
  // every render of an owner that passes a fresh arrow.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const setOpen = useCallback((next: boolean): void => {
    setOwnOpen(next);
    onOpenChangeRef.current?.(next);
  }, []);
  const sectionRef = useRef<HTMLElement>(null);
  const bodyId = `${useId()}-body`;

  // `#<id>` in the address bar — on arrival or from a link on this page —
  // unfolds the panel it names.
  useEffect(() => {
    if (!collapsible || id === undefined) return undefined;
    const reveal = (): void => {
      if (window.location.hash !== `#${id}`) return;
      setOpen(true);
      sectionRef.current?.scrollIntoView({ block: 'start' });
    };
    reveal();
    window.addEventListener('hashchange', reveal);
    return () => window.removeEventListener('hashchange', reveal);
  }, [collapsible, id, setOpen]);

  // {@link revealPanel}: an action elsewhere on the page that points here.
  useEffect(() => {
    if (!collapsible || id === undefined) return undefined;
    const reveal = (event: Event): void => {
      if (event instanceof CustomEvent && event.detail === id) setOpen(true);
    };
    window.addEventListener(REVEAL_EVENT, reveal);
    return () => window.removeEventListener(REVEAL_EVENT, reveal);
  }, [collapsible, id, setOpen]);

  // **Expand all** / **Collapse all** on a page of collapsible panels.
  useEffect(() => {
    if (!collapsible) return undefined;
    const apply = (event: Event): void => {
      if (!(event instanceof CustomEvent) || typeof event.detail !== 'boolean') return;
      const next = event.detail;
      setOpen(next);
      if (id !== undefined) writePanelOpen(id, next);
    };
    window.addEventListener(PANELS_EVENT, apply);
    return () => window.removeEventListener(PANELS_EVENT, apply);
  }, [collapsible, id, setOpen]);

  const classes = ['panel'];
  if (tone !== undefined) classes.push(`panel--${tone}`);
  if (collapsible) classes.push(open ? 'panel--open' : 'panel--collapsed');
  if (className !== undefined) classes.push(className);

  if (collapsible) {
    const toggle = (): void => {
      const next = !open;
      setOpen(next);
      if (id !== undefined) writePanelOpen(id, next);
    };
    // The button holds the title rather than being the header itself: the
    // header also carries `actions`, which are buttons, and a button cannot
    // nest another one. The button stretches over the header's free width,
    // so the header still reads and clicks as one control.
    return (
      <section className={classes.join(' ')} id={id} ref={sectionRef}>
        <header className="panel__header panel__header--toggle">
          <button
            type="button"
            className="panel__toggle"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={toggle}
          >
            <Icon name="chevron-right" className="panel__chevron" />
            <h2 className="panel__title">
              {icon !== undefined && <Icon name={icon} />}
              {title}
              {meta}
            </h2>
            {!open && summary !== undefined && <span className="panel__summary">{summary}</span>}
          </button>
          {open && actions !== undefined && <div className="panel__actions">{actions}</div>}
        </header>
        {open && (
          <div className="panel__body" id={bodyId}>
            {children}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={classes.join(' ')} id={id}>
      {(title !== undefined || actions !== undefined) && (
        <header className="panel__header">
          <h2 className="panel__title">
            {icon !== undefined && <Icon name={icon} />}
            {title}
            {meta}
          </h2>
          {actions !== undefined && <div className="panel__actions">{actions}</div>}
        </header>
      )}
      <div className="panel__body">{children}</div>
    </section>
  );
}

const panelKey = (id: string): string => `mc.panel.${id}`;

const PANELS_EVENT = 'mc:panels';

/** Opens or folds every collapsible panel on screen, and remembers it for each. */
export function setAllPanels(open: boolean): void {
  window.dispatchEvent(new CustomEvent<boolean>(PANELS_EVENT, { detail: open }));
}

const REVEAL_EVENT = 'mc:reveal-panel';

/**
 * Unfolds the panel with this id, if it is folded, and scrolls it into view.
 * A panel that is not collapsible is simply scrolled to, so an action can
 * point at a section without knowing which of the two it is on this page.
 */
export function revealPanel(id: string): void {
  window.dispatchEvent(new CustomEvent<string>(REVEAL_EVENT, { detail: id }));
  // After the frame the unfolded body is rendered in, so the scroll lands on
  // the panel at its open height.
  window.requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });
}

/**
 * A collapsible panel's open state, held by the component that renders it:
 * the remembered state (or `defaultOpen`) to begin with, and whatever the
 * panel reports through `onOpenChange` after that.
 */
export function usePanelOpen(
  id: string,
  defaultOpen = false,
): readonly [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => readPanelOpen(id, defaultOpen));
  return [open, setOpen];
}

/** Storage can throw (a private window, a full quota); the default stands in. */
function readPanelOpen(id: string | undefined, fallback: boolean): boolean {
  if (id === undefined) return fallback;
  try {
    const stored = window.localStorage.getItem(panelKey(id));
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
  } catch {
    // Fall through to the default.
  }
  return fallback;
}

function writePanelOpen(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(panelKey(id), open ? 'open' : 'closed');
  } catch {
    // Not remembered this time; the panel still toggles.
  }
}

/** Label / value pairs. */
export function Facts({
  items,
}: {
  readonly items: readonly { readonly label: string; readonly value: ReactNode; readonly mono?: boolean }[];
}) {
  return (
    <dl className="facts">
      {items.map((item) => (
        <div className="facts__row" key={item.label}>
          <dt>{item.label}</dt>
          <dd className={item.mono === true ? 'mono' : undefined}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Kbd({ children }: { readonly children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Skeleton({ lines = 3 }: { readonly lines?: number }) {
  return (
    <div className="skeleton" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <span className="skeleton__line" key={index} style={{ width: `${String(90 - (index % 3) * 18)}%` }} />
      ))}
    </div>
  );
}

/**
 * The one look of a wait on the server: a turning ring and a sentence saying
 * what is happening (US-010). `Skeleton` is for the first load of a page or
 * panel only; anything the server is busy doing reads through this.
 */
export function Loader({
  label,
  detail,
  size = 'md',
}: {
  readonly label: ReactNode;
  /** A muted second line: which step, how long, what comes next. */
  readonly detail?: ReactNode;
  readonly size?: 'sm' | 'md';
}) {
  return (
    <div className={`loader loader--${size}`} role="status" aria-live="polite">
      <span className="loader__ring" aria-hidden="true" />
      <div className="loader__text">
        <span className="loader__label">{label}</span>
        {detail !== undefined && detail !== null && <span className="loader__detail">{detail}</span>}
      </div>
    </div>
  );
}

/** Content that should be read but not shown. */
export function VisuallyHidden({ children }: { readonly children: ReactNode }) {
  return <span className="visually-hidden">{children}</span>;
}

/** A row of segmented choices; the product's filter control. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  readonly value: T;
  readonly options: readonly { readonly value: T; readonly label: ReactNode; readonly count?: number }[];
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
}) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={`segmented__option${option.value === value ? ' segmented__option--active' : ''}`}
          key={option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined && <span className="segmented__count">{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Markdown, as much of it as `markdown.ts` reads: headings, paragraphs, lists
 * and fenced code. Nothing here produces HTML — the blocks are React nodes, so
 * the text is escaped by React and a plan that quotes a `<script>` is a plan
 * that reads `<script>`.
 */
export function Markdown({ text }: { readonly text: string }) {
  const blocks: MarkdownBlock[] = parseMarkdown(text);
  if (blocks.length === 0) return <p className="markdown markdown--empty">Nothing to show yet.</p>;
  return (
    <div className="markdown">
      {blocks.map((block, index) => {
        if (block.kind === 'code') {
          return (
            <pre className="output output--wrap" key={index}>
              {block.text}
            </pre>
          );
        }
        if (block.kind === 'list') {
          const items = block.items.map((runs, itemIndex) => <li key={itemIndex}>{runs.map(renderRun)}</li>);
          return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
        }
        if (block.kind === 'heading') {
          const Heading = `h${String(block.level)}` as 'h1' | 'h2' | 'h3';
          return <Heading key={index}>{block.runs.map(renderRun)}</Heading>;
        }
        return <p key={index}>{block.runs.map(renderRun)}</p>;
      })}
    </div>
  );
}

function renderRun(run: TextRun, index: number) {
  if (run.code) {
    return (
      <code className="mono" key={index}>
        {run.text}
      </code>
    );
  }
  if (run.strong) return <strong key={index}>{run.text}</strong>;
  return <span key={index}>{run.text}</span>;
}
